
import { listArchivedThreads, type ArchivedThread } from "../api/threads.js";

type Host = HTMLElement & {
  threads: ArchivedThread[];
  loaded: boolean;
  error: string;
};

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function formatDate(ms: number): string {
  if (!ms) return "unknown";
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium" }).format(new Date(ms));
}

async function refresh(host: Host): Promise<void> {
  try {
    host.threads = await listArchivedThreads();
    host.loaded = true;
    host.error = "";
  } catch {
    host.error = "Failed to load archived threads.";
  }
}

const styles = css`
  :host {
    display: block;
    height: 100%;
    min-height: 0;
    overflow: auto;
  }

  .root {
    box-sizing: border-box;
    min-height: 100%;
    padding: 32px 24px 64px;
  }

  .shell {
    display: flex;
    flex-direction: column;
    gap: 20px;
    margin: 0 auto;
    max-width: 760px;
    width: 100%;
  }

  h1,
  p {
    margin: 0;
  }

  .page-title {
    color: var(--foreground);
    font-size: 26px;
    letter-spacing: -0.02em;
    line-height: 1.2;
  }

  .lead {
    color: var(--muted-foreground);
    font-size: 14px;
    line-height: 1.5;
    margin-top: 6px;
  }

  .list {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .row {
    align-items: center;
    border: 1px solid var(--border);
    border-radius: 10px;
    display: flex;
    gap: 16px;
    justify-content: space-between;
    padding: 12px 14px;
  }

  .info {
    min-width: 0;
  }

  .name {
    color: var(--foreground);
    font-size: 14px;
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .meta {
    color: var(--muted-foreground);
    font-size: 12px;
    margin-top: 3px;
  }

  .row-menu {
    align-items: center;
    background: transparent;
    border-radius: 6px;
    color: var(--muted-foreground);
    cursor: pointer;
    display: inline-flex;
    flex: 0 0 auto;
    font-size: 18px;
    height: 32px;
    justify-content: center;
    line-height: 1;
    user-select: none;
    width: 32px;
  }

  .row-menu:hover {
    background: var(--accent);
    color: var(--accent-foreground);
  }

  .muted {
    color: var(--muted-foreground);
    font-size: 14px;
    line-height: 1.5;
  }

  @media (max-width: 760px) {
    .root {
      padding: 24px 16px 48px;
    }
  }
`;

export class AppArchivedThreads extends HTMLElement {
  static argonHydrate(element) {
    if (!(element instanceof AppArchivedThreads)) return;
    if (element.isConnected && !element.#bindingsMounted) {
      element.#syncHydratedProps();
      element.connectedCallback();
    }
    if (element.#root) element.#wireEvents(element.#root);
  }
  static observedAttributes = ["threads","data-threads","loaded","data-loaded","error","data-error"];

  #threads = [];
  #loaded = false;
  #error = "";
  #threadsSet = false;
  #loadedSet = false;
  #errorSet = false;
  #textStart0;
  #textEnd0;
  #textLast0;
  #hooksLive = false;
  #cleanups = [];
  #root;
  #bindingsMounted = false;
  #hydrating = false;
  #isCSR = false;

  get threads() {
    return this.#threads;
  }

  set threads(value) {
    const raw = value ?? [];
    const next = raw == null ? raw : typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (Object.is(this.#threads, next)) return;
    this.#threads = next;
    this.#threadsSet = true;
    this.#updateBindings('threads');
  }

  get loaded() {
    return this.#loaded;
  }

  set loaded(value) {
    const raw = value ?? false;
    const next = raw == null ? raw : typeof raw === 'string' ? raw !== 'false' : Boolean(raw);
    if (Object.is(this.#loaded, next)) return;
    this.#loaded = next;
    this.#loadedSet = true;
    this.#updateBindings('loaded');
  }

  get error() {
    return this.#error;
  }

  set error(value) {
    const raw = value ?? "";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#error, next)) return;
    this.#error = next;
    this.#errorSet = true;
    this.#updateBindings('error');
  }

  #syncHydratedProps() {
    if (this.#isCSR) return;
    if (this.hasAttribute('data-threads')) this.threads = this.dataset.threads;
    if (this.hasAttribute('data-loaded')) this.loaded = this.dataset.loaded;
    if (this.hasAttribute('data-error')) this.error = this.dataset.error;
  }

  constructor(props) {
    super();
    if (props !== undefined) {
      this.#isCSR = true;
      this.threads = props.threads;
      this.loaded = props.loaded;
      this.error = props.error;
      this.#initState();
      const shadow = this.attachShadow({ mode: 'open' });
      shadow.innerHTML = this.#render();
      this.#mount(shadow, false);
    }
  }

  connectedCallback() {
    if (!this.#isCSR && !this.#bindingsMounted) {
      this.#syncHydratedProps();
    if (!this.#threadsSet) this.threads = this.dataset.threads;
    if (!this.#loadedSet) this.loaded = this.dataset.loaded;
    if (!this.#errorSet) this.error = this.dataset.error;
    this.#initState();
    const shadow = this.shadowRoot;
    const root = shadow ?? this.attachShadow({ mode: 'open' });
    if (!shadow) root.innerHTML = this.#render();
    this.#mount(root, !!shadow);
    }
    if (this.#hooksLive) return;
    this.#hooksLive = true;
    {
      const cleanup = (() => {
    void refresh(this);
})();
      if (typeof cleanup === 'function') this.#cleanups.push(cleanup);
    }
  }

  disconnectedCallback() {
    this.#hooksLive = false;
    for (const cleanup of this.#cleanups.splice(0)) cleanup();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    switch (name) {
      case 'threads':
      case 'data-threads':
        this.threads = newValue;
        break;
      case 'loaded':
      case 'data-loaded':
        this.loaded = newValue;
        break;
      case 'error':
      case 'data-error':
        this.error = newValue;
        break;
    }
  }

  #initState() {

  }

  #render() {
    const rows = this.#threads.map((thread) => ({
    id: thread.id,
    title: escapeHtml(thread.title || "Untitled"),
    meta: `Last active ${formatDate(thread.last_activity_at)} · Archived ${formatDate(thread.archived_at)}`,
}));
    return `<style>${this.#escText(styles)}</style><div class="root" data-argon="3167"><div class="shell"><header><h1 class="page-title">Archived threads</h1><p class="lead">Archived threads are hidden from the sidebar but keep their messages and files. Unarchive to bring one back, or delete it permanently.</p></header><!--argon-text:0-->${this.#error ? `<p class="muted">${this.#escText(this.#error)}</p>` : this.#escText("")}${(rows.length > 0) ? `<div class="list">${rows.map((row) => `<div class="row"><div class="info"><div class="name">${row.title}</div><div class="meta">${row.meta}</div></div><span class="row-menu" role="button" title="Thread actions" aria-label="Thread actions" data-action="thread-menu" data-thread-id="${row.id}">⋯</span></div>`).join("")}</div>` : `<p class="muted">${this.#escText(this.#loaded ? "No archived threads yet." : "Loading…")}</p>`}<!--/argon-text:0--></div></div>`;
  }



  #mount(root, hydrating) {
    this.#root = root;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_COMMENT);
    let marker;
    while ((marker = walker.nextNode())) {
      switch (marker.nodeValue) {
        case 'argon-text:0':
          this.#textStart0 = marker;
          break;
        case '/argon-text:0':
          this.#textEnd0 = marker;
          break;
      }
    }
    this.#bindingsMounted = true;
    this.#hydrating = hydrating;
    this.#updateBindings();
    this.#hydrating = false;
    this.#wireEvents(root);
    if (!root.__argonEventObserver) {
      const observer = new window.MutationObserver(() => this.#wireEvents(root));
      observer.observe(root, { childList: true, subtree: true });
      Object.defineProperty(root, '__argonEventObserver', { value: observer });
    }
    this.setAttribute('hydrated', '');
    setTimeout(() => { if (this.#root === root) this.#wireEvents(root); }, 0);
  }

  #replaceRange(start, end, value) {
    if (!start || !end) return;
    const html = String(value);
    const sole = start.nextSibling;
    if (sole && sole !== end && sole.nextSibling === end && sole.nodeType === 3 && !/[<&]/.test(html)) {
      if (html.startsWith(sole.data)) sole.appendData(html.slice(sole.data.length));
      else sole.data = html;
      return;
    }
    const template = document.createElement('template');
    template.innerHTML = html;
    if (sole && sole !== end && sole.nextSibling === end && template.content.childNodes.length === 1 && this.#patchElement(sole, template.content.firstChild)) return;
    let oldNode = start.nextSibling;
    let newNode = template.content.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode || oldNode === end) {
        end.before(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldNode.before(newNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode && oldNode !== end) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }

  #patchElement(oldEl, newEl) {
    if (!oldEl || !newEl || oldEl.nodeType !== 1 || newEl.nodeType !== 1 || oldEl.localName !== newEl.localName) return false;
    for (const attr of [...oldEl.attributes]) {
      if (attr.name !== 'hydrated' && !newEl.hasAttribute(attr.name)) oldEl.removeAttribute(attr.name);
    }
    for (const attr of [...newEl.attributes]) {
      if (oldEl.getAttribute(attr.name) !== attr.value) oldEl.setAttribute(attr.name, attr.value);
    }
    this.#patchChildren(oldEl, newEl);
    this.#wireEvents(oldEl);
    return true;
  }

  #patchNode(oldNode, newNode) {
    if (!oldNode || !newNode) return false;
    if (oldNode.nodeType === 3 && newNode.nodeType === 3) {
      if (oldNode.data !== newNode.data) oldNode.data = newNode.data;
      return true;
    }
    if (oldNode.nodeType === 8 && newNode.nodeType === 8) {
      if (oldNode.nodeValue !== newNode.nodeValue) oldNode.nodeValue = newNode.nodeValue;
      return true;
    }
    return this.#patchElement(oldNode, newNode);
  }

  #patchChildren(oldEl, newEl) {
    let oldNode = oldEl.firstChild;
    let newNode = newEl.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode) {
        oldEl.append(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldEl.insertBefore(newNode, oldNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }
  #escText(v) {
    return String(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }


  #eventTargets(scope, id) {
    const selector = `[data-argon="${id}"]`;
    const targets = [...scope.querySelectorAll(selector)];
    if (scope.nodeType === 1 && scope.matches(selector)) targets.unshift(scope);
    return targets;
  }

  #listen(el, id, event, handler) {
    const key = id + ':' + event;
    const root = this.#root;
    if (!root.__argonListeners) Object.defineProperty(root, '__argonListeners', { value: {} });
    const prev = root.__argonListeners[key];
    if (prev) root.removeEventListener(event, prev, true);
    const next = event => {
      const target = event.composedPath().find(node => node?.getAttribute?.('data-argon') === id);
      if (!target) return;
      const delegated = new Proxy(event, { get(source, prop) {
        if (prop === 'currentTarget') return target;
        const value = Reflect.get(source, prop, source);
        return typeof value === 'function' ? value.bind(source) : value;
      } });
      handler.call(target, delegated);
    };
    root.__argonListeners[key] = next;
    root.addEventListener(event, next, true);
  }

  #updateBindings(prop) {
    if (!this.#bindingsMounted) return;
    const rows = this.#threads.map((thread) => ({
    id: thread.id,
    title: escapeHtml(thread.title || "Untitled"),
    meta: `Last active ${formatDate(thread.last_activity_at)} · Archived ${formatDate(thread.archived_at)}`,
}));
    if (prop === undefined || prop === 'error' || prop === 'threads' || prop === 'loaded') {
      const __argonNext = `${this.#error ? `<p class="muted">${this.#escText(this.#error)}</p>` : this.#escText("")}${(rows.length > 0) ? `<div class="list">${rows.map((row) => `<div class="row"><div class="info"><div class="name">${row.title}</div><div class="meta">${row.meta}</div></div><span class="row-menu" role="button" title="Thread actions" aria-label="Thread actions" data-action="thread-menu" data-thread-id="${row.id}">⋯</span></div>`).join("")}</div>` : `<p class="muted">${this.#escText(this.#loaded ? "No archived threads yet." : "Loading…")}</p>`}`;
      if (__argonNext !== this.#textLast0) {
        this.#textLast0 = __argonNext;
        this.#replaceRange(this.#textStart0, this.#textEnd0, __argonNext);
      }
    }
  }

  #wireEvents(scope) {
    const root = this.#root;
    this.#eventTargets(scope, '3167').forEach(el => this.#listen(el, '3167', 'click', (event: Event) => {
    const trigger = (event.target as HTMLElement).closest<HTMLElement>('[data-action="thread-menu"]');
    if (!trigger)
        return;
    const id = trigger.dataset.threadId ?? "";
    const thread = this.#threads.find((item) => item.id === id);
    if (!thread)
        return;
    this.dispatchEvent(new CustomEvent("thread-menu", { bubbles: true, composed: true, detail: { id, title: thread.title, archived: true, anchor: trigger } }));
}));
    const roots = [scope];
    for (let index = 0; index < roots.length; index++) {
      const candidates = roots[index].nodeType === 1 ? [roots[index], ...roots[index].querySelectorAll('*')] : [...roots[index].querySelectorAll('*')];
      for (const element of candidates) {
        customElements.upgrade(element);
        const hydrate = () => {
          customElements.upgrade(element);
          const definition = customElements.get(element.localName);
          definition?.argonHydrate?.(element);
        };
        if (element.isConnected && customElements.get(element.localName)) hydrate();
        else if (element.localName.includes('-')) customElements.whenDefined(element.localName).then(hydrate);
        else queueMicrotask(hydrate);
        if (element.shadowRoot) roots.push(element.shadowRoot);
      }
    }
  }
}
